import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";
import { PaisInput } from "@/components/PaisInput";
import { InstituicaoInput } from "@/components/InstituicaoInput";
import { GrauInput } from "@/components/GrauInput";
import { QrCode, Users, Copy, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  pendente: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  aprovado: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  rejeitado: "bg-red-500/20 text-red-300 border-red-500/40",
};

export function InscricaoManager({ turma, linkLabel = "Inscrição" }) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [insOpen, setInsOpen] = useState(false);
  const [link, setLink] = useState(null);
  const [expira, setExpira] = useState("");
  const [copied, setCopied] = useState(false);
  const [inscricoes, setInscricoes] = useState([]);
  const [listas, setListas] = useState({ grau: [], instituicao: [], unidade: [], pais: [] });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const url = (tok) => `${window.location.origin}/inscricao/${tok}`;

  const openLink = async () => {
    setLinkOpen(true);
    const { data } = await api.get(`/turmas/${turma.id}/inscricao-link`);
    setLink(data);
    setExpira(data?.expira_em || "");
  };
  const gerar = async () => {
    const { data } = await api.post(`/turmas/${turma.id}/inscricao-link`, { expira_em: expira || null });
    setLink(data);
    toast.success("Link gerado");
  };
  const toggleAtivo = async () => {
    const { data } = await api.put(`/turmas/${turma.id}/inscricao-link`, { ativo: !link.ativo });
    setLink(data);
  };
  const salvarExpira = async () => {
    const { data } = await api.put(`/turmas/${turma.id}/inscricao-link`, { expira_em: expira || "" });
    setLink(data);
    toast.success("Validade atualizada");
  };
  const copiar = () => {
    navigator.clipboard.writeText(url(link.token));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Link copiado");
  };

  const openIns = async () => {
    setInsOpen(true);
    load();
    ["grau", "instituicao", "unidade", "pais"].forEach((t) =>
      api.get(`/listas/${t}`).then((r) => setListas((s) => ({ ...s, [t]: r.data }))));
  };
  const load = () => api.get(`/inscricoes?turma_id=${turma.id}`).then((r) => setInscricoes(r.data));
  const aprovar = async (i) => {
    try { await api.post(`/inscricoes/${i.id}/aprovar`); toast.success("Inscrição aprovada"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const rejeitar = async (i) => {
    if (!window.confirm(`Rejeitar a inscrição de "${i.nome}"?`)) return;
    await api.post(`/inscricoes/${i.id}/rejeitar`); toast.success("Inscrição rejeitada"); load();
  };
  const startEdit = (i) => {
    setEditing(i.id);
    setEditForm({ nome: i.nome || "", nome_guerra: i.nome_guerra || "", identificacao: i.identificacao || "", grau_nome: i.grau_nome || "", instituicao_nome: i.instituicao_nome || "", unidade_id: i.unidade_id || "", pais_id: i.pais_id || "", pais_nome: i.pais_nome || "" });
  };
  const salvarEdit = async () => {
    const paisNome = (editForm.pais_nome || "").trim();
    const paisMatch = listas.pais.find((p) => (p.nome || "").toLowerCase() === paisNome.toLowerCase());
    const instNome = (editForm.instituicao_nome || "").trim();
    const instMatch = listas.instituicao.find((it) => (it.nome || "").toLowerCase() === instNome.toLowerCase());
    const grauNome = (editForm.grau_nome || "").trim();
    const grauMatch = listas.grau.find((g) => (g.nome || "").toLowerCase() === grauNome.toLowerCase());
    await api.put(`/inscricoes/${editing}`, {
      ...editForm,
      grau_id: grauMatch ? grauMatch.id : null, grau_nome: grauNome || null,
      instituicao_nome: instNome || null, instituicao_id: instMatch ? instMatch.id : "",
      unidade_id: editForm.unidade_id || null,
      pais_nome: paisNome || null, pais_id: paisMatch ? paisMatch.id : null,
    });
    toast.success("Inscrição atualizada");
    setEditing(null);
    load();
  };

  return (
    <>
      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="secondary" data-testid={`link-turma-${turma.id}`} onClick={openLink}><QrCode className="h-3.5 w-3.5 mr-1.5" />{linkLabel}</Button>
        <Button size="sm" variant="secondary" data-testid={`inscritos-turma-${turma.id}`} onClick={openIns}><Users className="h-3.5 w-3.5 mr-1.5" />Inscritos</Button>
      </div>

      {/* Link / QR */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="bg-popover border-border">
          <DialogHeader><DialogTitle>Link de inscrição — {turma.nome}</DialogTitle></DialogHeader>
          {!link ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Nenhum link gerado ainda.</p>
              <div>
                <Label>Data de expiração (opcional)</Label>
                <Input type="date" data-testid="link-expira" value={expira} onChange={(e) => setExpira(e.target.value)} />
              </div>
              <Button data-testid="gerar-link-btn" onClick={gerar}>Gerar link de inscrição</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center p-4 bg-white rounded-lg" data-testid="qr-code">
                <QRCodeCanvas value={url(link.token)} size={180} />
              </div>
              <div className="flex gap-2">
                <Input readOnly data-testid="link-url" value={url(link.token)} className="text-xs" />
                <Button size="icon" data-testid="copiar-link-btn" onClick={copiar}>{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
              </div>
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <div>
                  <Label>Link ativo</Label>
                  {!link.ativo && <p className="text-xs text-amber-300">Desativado — novos cadastros bloqueados</p>}
                </div>
                <Switch data-testid="link-ativo-toggle" checked={link.ativo} onCheckedChange={toggleAtivo} />
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>Expira em</Label>
                  <Input type="date" data-testid="link-expira-edit" value={expira} onChange={(e) => setExpira(e.target.value)} />
                </div>
                <Button variant="secondary" data-testid="salvar-expira-btn" onClick={salvarExpira}>Salvar</Button>
              </div>
              <Button variant="outline" className="w-full" data-testid="regerar-link-btn" onClick={gerar}>Gerar novo link (invalida o atual)</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Inscritos */}
      <Dialog open={insOpen} onOpenChange={setInsOpen}>
        <DialogContent className="bg-popover border-border max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Inscritos — {turma.nome}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {inscricoes.map((i) => (
              <div key={i.id} data-testid={`inscricao-row-${i.id}`} className="border border-border rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2">
                      {i.nome}
                      {i.nome_guerra && <span className="text-xs text-muted-foreground">"{i.nome_guerra}"</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{i.identificacao} · {i.grau_nome || "—"} · {i.instituicao_nome || "—"}{i.unidade_nome ? ` / ${i.unidade_nome}` : ""}</div>
                    <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{(i.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <Badge className={STATUS_BADGE[i.status]} data-testid={`ins-status-${i.id}`}>{i.status}</Badge>
                </div>
                {i.possivel_duplicidade && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-300 mt-2" data-testid={`ins-dup-${i.id}`}>
                    <AlertTriangle className="h-3.5 w-3.5" /> Possível duplicidade nesta turma (RG ou nome já existe)
                  </div>
                )}
                {i.status === "pendente" && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" data-testid={`aprovar-${i.id}`} onClick={() => aprovar(i)}><Check className="h-3.5 w-3.5 mr-1" />Aprovar</Button>
                    <Button size="sm" variant="secondary" data-testid={`editar-ins-${i.id}`} onClick={() => startEdit(i)}>Editar</Button>
                    <Button size="sm" variant="outline" data-testid={`rejeitar-${i.id}`} onClick={() => rejeitar(i)}><X className="h-3.5 w-3.5 mr-1" />Rejeitar</Button>
                  </div>
                )}
                {i.status === "aprovado" && i.aprovado_por_nome && (
                  <div className="text-[11px] text-muted-foreground mt-1">Aprovado por {i.aprovado_por_nome}</div>
                )}
              </div>
            ))}
            {!inscricoes.length && <p className="text-sm text-muted-foreground">Nenhuma inscrição recebida.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Editar inscrição */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-popover border-border">
          <DialogHeader><DialogTitle>Editar inscrição</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input data-testid="edit-ins-nome" value={editForm.nome || ""} onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nome de guerra</Label><Input data-testid="edit-ins-guerra" value={editForm.nome_guerra || ""} onChange={(e) => setEditForm({ ...editForm, nome_guerra: e.target.value })} /></div>
              <div><Label>RG / Id.</Label><Input data-testid="edit-ins-id" value={editForm.identificacao || ""} onChange={(e) => setEditForm({ ...editForm, identificacao: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Grau</Label>
                <GrauInput value={editForm.grau_nome} onChange={(v) => setEditForm({ ...editForm, grau_nome: v })} opcoes={listas.grau} testid="edit-ins-grau" label={null} />
              </div>
              <div>
                <Label>Instituição</Label>
                <InstituicaoInput
                  value={editForm.instituicao_nome}
                  onChange={(v) => setEditForm({ ...editForm, instituicao_nome: v })}
                  opcoes={listas.instituicao}
                  testid="edit-ins-instituicao"
                  label={null}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Unidade</Label>
                <Select value={editForm.unidade_id || ""} onValueChange={(v) => setEditForm({ ...editForm, unidade_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">{listas.unidade.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <PaisInput
                value={editForm.pais_nome}
                onChange={(v) => setEditForm({ ...editForm, pais_nome: v })}
                opcoes={listas.pais}
                testid="ins-edit-pais"
              />
            </div>
          </div>
          <DialogFooter><Button data-testid="salvar-edit-ins" onClick={salvarEdit}>Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
